// gti-chms-frontend/src/components/FinancialRecordFormModal.tsx
import React, { useState, useEffect } from 'react';
import type { FinancialRecord, Account } from '../../models';

// Define form data type
interface FinancialRecordFormData {
    recordType: 'Income' | 'Expense' | 'Donation' | ''; // Allow empty for initial selection
    category: string;
    amount: number;
    currency: string;
    recordDate: string; // YYYY-MM-DD format for input type="date"
    description: string;
    accountId: string; // The selected account's ID
}

interface FinancialRecordFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (recordData: Omit<FinancialRecordFormData, 'recordDate'> & { recordDate: Date | string }, recordId?: string) => Promise<void>;
    recordToEdit?: FinancialRecord | null; // Optional: record data if in edit mode
    accounts: Account[]; // List of available accounts to choose from
}

const FinancialRecordFormModal: React.FC<FinancialRecordFormModalProps> = ({ isOpen, onClose, onSave, recordToEdit, accounts }) => {
    const isEditing = !!recordToEdit;
    const [formData, setFormData] = useState<FinancialRecordFormData>({
        recordType: '',
        category: '',
        amount: 0,
        currency: 'SZL', // Default currency, adjust as needed
        recordDate: new Date().toISOString().split('T')[0], // Default to today's date
        description: '',
        accountId: '', // Default to empty
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Populate form data when recordToEdit changes (for edit mode)
    useEffect(() => {
        if (isEditing && recordToEdit) {
            setFormData({
                recordType: recordToEdit.recordType,
                category: recordToEdit.category,
                amount: recordToEdit.amount,
                currency: recordToEdit.currency,
                recordDate: new Date(recordToEdit.recordDate).toISOString().split('T')[0], // Format for input type="date"
                description: recordToEdit.description || '',
                accountId: recordToEdit.accountId, // Set the ID of the associated account

            });
        } else {
            // Reset form for add mode
            setFormData({
                recordType: '',
                category: '',
                amount: 0,
                currency: 'SZL',
                recordDate: new Date().toISOString().split('T')[0],
                description: '',
                accountId: accounts.length > 0 ? accounts[0]._id : '', // Pre-select first account if available
            });
        }
        setError(null);
        setSuccess(null);
    }, [isOpen, isEditing, recordToEdit, accounts]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'amount' ? parseFloat(value) || 0 : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setLoading(true);

        // Basic client-side validation
        if (!formData.recordType || !formData.category || formData.amount <= 0 || !formData.currency || !formData.recordDate || !formData.accountId) {
            setError('Please fill in all required fields and ensure amount is positive.');
            setLoading(false);
            return;
        }

        try {
            await onSave({ ...formData, recordDate: new Date(formData.recordDate) }, isEditing ? recordToEdit?._id : undefined);
            setSuccess(`Financial record ${isEditing ? 'updated' : 'added'} successfully!`);
            setTimeout(() => {
                onClose(); // Close modal after success
            }, 1500);
        } catch (err: any) {
            console.error("Save Financial Record Error:", err);
            setError(err.response?.data?.message || err.message || `Failed to ${isEditing ? 'update' : 'add'} financial record.`);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
            <div className="relative p-8 bg-white w-full max-w-2xl mx-auto rounded-lg shadow-xl animate-scale-in">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
                >
                    &times;
                </button>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                    {isEditing ? 'Edit Financial Record' : 'Add New Financial Record'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="recordType" className="block text-sm font-medium text-gray-700">Record Type</label>
                            <select name="recordType" id="recordType" value={formData.recordType} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                                <option value="">Select Type</option>
                                <option value="Income">Income</option>
                                <option value="Expense">Expense</option>
                                <option value="Donation">Donation</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
                            <input type="text" name="category" id="category" value={formData.category} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                        <div>
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount</label>
                            <input type="number" name="amount" id="amount" value={formData.amount} onChange={handleChange} required
                                min="0.01" step="0.01"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                        <div>
                            <label htmlFor="recordDate" className="block text-sm font-medium text-gray-700">Date</label>
                            <input type="date" name="recordDate" id="recordDate" value={formData.recordDate} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                        {/* NEW: Account Dropdown */}
                        <div>
                            <label htmlFor="accountId" className="block text-sm font-medium text-gray-700">Account</label>
                            <select name="accountId" id="accountId" value={formData.accountId} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                disabled={accounts.length === 0} // Disable if no accounts are available
                                >
                                <option value="">Select Account</option>
                                {accounts.map(account => (
                                    <option key={account._id} value={account._id}>
                                        {account.name} ({account.accountIdentifier}) - {account.currency} {account.currentBalance.toFixed(2)}
                                    </option>
                                ))}
                            </select>
                            {accounts.length === 0 && (
                                <p className="text-red-500 text-xs mt-1">No accounts available. Please add an account first (Manage Accounts section).</p>
                            )}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description (Optional)</label>
                        <textarea name="description" id="description" value={formData.description} onChange={handleChange} rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                    </div>

                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm" role="alert">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative text-sm" role="alert">
                            {success}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-4">
                        <button type="button" onClick={onClose} disabled={loading}
                            className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading || accounts.length === 0}
                            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50">
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                isEditing ? 'Update Record' : 'Add Record'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FinancialRecordFormModal;
